/* eslint-disable @next/next/no-img-element */
import React, {
  useRef, useState, useCallback, useEffect,
} from 'react';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import { V3CanvasHomeCaseItem } from '$/domains/caseV3/type';
import PromptCaseCard from './FeaturedCase';
import styles from './index.module.scss';

type Props = {
  list: V3CanvasHomeCaseItem[];
  groupId: number;
  groupName: string;
  groupDesc: string;
  onCardClick?: (item: V3CanvasHomeCaseItem) => void;
  onTryButtonClick?: (item: V3CanvasHomeCaseItem) => void;
  onLikeClick?: (item: V3CanvasHomeCaseItem) => void;
};

/**
 * 渲染 BaseHomeNew 推荐卡片列表。
 */
function FeaturedCaseList({
  list, groupId, groupName, groupDesc, onCardClick, onTryButtonClick, onLikeClick,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const categoryTypeRef = useRef<HTMLDivElement>(null);
  const [caseCardHeight, setCaseCardHeight] = useState<number>();
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  /**
   * 根据列表实际滚动宽度更新左右翻页按钮状态。
   */
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    setHasOverflow(overflow);
    setCanScrollLeft(overflow && el.scrollLeft > 1);
    setCanScrollRight(overflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(updateScrollState);
    const el = scrollRef.current;
    if (!el) {
      return () => window.cancelAnimationFrame(frameId);
    }
    el.addEventListener('scroll', updateScrollState);
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      window.cancelAnimationFrame(frameId);
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, list]);

  useEffect(() => {
    const categoryEl = categoryTypeRef.current;
    if (!categoryEl) return undefined;
    const cardEl = categoryEl.nextElementSibling as HTMLElement | null;
    if (!cardEl) return undefined;

    const updateCaseCardHeight = () => {
      setCaseCardHeight(cardEl.getBoundingClientRect().height);
    };

    const frameId = window.requestAnimationFrame(updateCaseCardHeight);
    const resizeObserver = new ResizeObserver(updateCaseCardHeight);
    resizeObserver.observe(cardEl);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, [list]);

  /**
   * 按当前可见宽度翻页，保留一张卡片作为视觉锚点。
   */
  const scrollBy = useCallback((direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    const pageStep = Math.max(el.clientWidth - 168, 168);
    el.scrollBy({ left: direction * pageStep, behavior: 'smooth' });
  }, []);

  if (!list || list.length === 0) {
    return null;
  }

  return (
    <div className={styles.FeaturedCaseListWrapper}>
      {hasOverflow && (
        <button
          type="button"
          className={classNames(styles.scrollBtn, styles.scrollBtnLeft, {
            [styles.scrollBtnDisabled]: !canScrollLeft,
          })}
          onClick={() => scrollBy(-1)}
          disabled={!canScrollLeft}
          aria-label="上一组推荐案例"
        >
          <IconFont name="arrowLeftBold" fontSize={16} />
        </button>
      )}
      <div className={styles.FeaturedCaseList} ref={scrollRef}>

        <div
          ref={categoryTypeRef}
          className={styles.categoryType}
          style={caseCardHeight ? { height: caseCardHeight, maxHeight: caseCardHeight } : undefined}
        >
          <div className={styles.categoryName}>{groupName}</div>
          <div className={styles.categoryDesc}>{groupDesc}</div>
        </div>
        {list.map((item, index) => (
          <PromptCaseCard
            key={item.case_id}
            item={item}
            positionId={index}
            categoryId={groupId}
            tag={groupName}
            onCardClick={onCardClick}
            onTryButtonClick={onTryButtonClick}
            onLikeClick={onLikeClick}
          />
        ))}
      </div>
      {hasOverflow && (
        <button
          type="button"
          className={classNames(styles.scrollBtn, styles.scrollBtnRight, {
            [styles.scrollBtnDisabled]: !canScrollRight,
          })}
          onClick={() => scrollBy(1)}
          disabled={!canScrollRight}
          aria-label="下一组推荐案例"
        >
          <IconFont name="arrowRightBold" fontSize={16} />
        </button>
      )}
    </div>
  );
}

export default React.memo(FeaturedCaseList);
